import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import useLocalStorage from "./useLocalStorage";
import { TIMER_PRESETS } from "../config/timers";
import * as api from "../services/api";

const MAX_LOCAL_PRESETS = 50;

export default function useWorkoutPresets() {
  const { isAuthenticated } = useAuth();

  // Local storage for unauthenticated users
  const [localPresets, setLocalPresets] = useLocalStorage("timerPresets", []);

  // API-backed state for authenticated users
  const [remotePresets, setRemotePresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch from API when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    api
      .getTimerPresets()
      .then((data) => {
        if (!cancelled) setRemotePresets(data);
      })
      .catch((err) => {
        if (!cancelled) setError("Failed to load presets");
        console.error("Failed to fetch timer presets:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const customPresets = isAuthenticated ? remotePresets : localPresets;

  const recentPresets = useMemo(
    () =>
      [...customPresets]
        .filter((p) => p.last_used_at)
        .sort((a, b) => new Date(b.last_used_at) - new Date(a.last_used_at))
        .slice(0, 5),
    [customPresets],
  );

  const clearError = useCallback(() => setError(null), []);

  // ─── CRUD ──────────────────────────────────────────────

  const savePreset = useCallback(
    async ({ name, mode, settings, is_default }) => {
      const now = new Date().toISOString();

      if (isAuthenticated) {
        // Optimistic: add placeholder, then replace with server response
        const tempId = `temp-${Date.now()}`;
        const optimistic = {
          id: tempId,
          name,
          mode,
          settings,
          is_default: is_default || false,
          last_used_at: null,
          created_at: now,
          updated_at: now,
        };
        setRemotePresets((prev) => [optimistic, ...prev]);
        try {
          const created = await api.createTimerPreset({
            name,
            mode,
            settings,
            is_default,
          });
          setRemotePresets((prev) =>
            prev.map((p) => (p.id === tempId ? created : p)),
          );
          return created;
        } catch (err) {
          setRemotePresets((prev) => prev.filter((p) => p.id !== tempId));
          setError("Failed to save preset");
          throw err;
        }
      } else {
        const preset = {
          id: `local-${Date.now()}`,
          name,
          mode,
          settings,
          is_default: is_default || false,
          last_used_at: null,
          created_at: now,
          updated_at: now,
        };
        setLocalPresets((prev) => [preset, ...prev].slice(0, MAX_LOCAL_PRESETS));
        return preset;
      }
    },
    [isAuthenticated, setLocalPresets],
  );

  const updatePreset = useCallback(
    async (id, updates) => {
      if (isAuthenticated) {
        const prev = remotePresets;
        setRemotePresets((list) =>
          list.map((p) =>
            p.id === id
              ? { ...p, ...updates, updated_at: new Date().toISOString() }
              : p,
          ),
        );
        try {
          const updated = await api.updateTimerPreset(id, updates);
          setRemotePresets((list) =>
            list.map((p) => (p.id === id ? updated : p)),
          );
          return updated;
        } catch (err) {
          setRemotePresets(prev);
          setError("Failed to update preset");
          throw err;
        }
      } else {
        let result;
        setLocalPresets((list) =>
          list.map((p) => {
            if (p.id === id) {
              result = {
                ...p,
                ...updates,
                updated_at: new Date().toISOString(),
              };
              return result;
            }
            return p;
          }),
        );
        return result;
      }
    },
    [isAuthenticated, remotePresets, setLocalPresets],
  );

  const deletePreset = useCallback(
    async (id) => {
      if (isAuthenticated) {
        const prev = remotePresets;
        setRemotePresets((list) => list.filter((p) => p.id !== id));
        try {
          await api.deleteTimerPreset(id);
        } catch (err) {
          setRemotePresets(prev);
          setError("Failed to delete preset");
          throw err;
        }
      } else {
        setLocalPresets((list) => list.filter((p) => p.id !== id));
      }
    },
    [isAuthenticated, remotePresets, setLocalPresets],
  );

  const touchPreset = useCallback(
    (id) => {
      const now = new Date().toISOString();
      if (isAuthenticated) {
        setRemotePresets((list) =>
          list.map((p) => (p.id === id ? { ...p, last_used_at: now } : p)),
        );
        api.touchTimerPreset(id).catch(() => {});
      } else {
        setLocalPresets((list) =>
          list.map((p) => (p.id === id ? { ...p, last_used_at: now } : p)),
        );
      }
    },
    [isAuthenticated, setLocalPresets],
  );

  // ─── Helpers ───────────────────────────────────────────

  const getPresetsForMode = useCallback(
    (mode) => customPresets.filter((p) => p.mode === mode),
    [customPresets],
  );

  const isDirty = useCallback((mode, currentSettings) => {
    const defaults = TIMER_PRESETS[mode]?.settings;
    if (!defaults) return false;
    const keys = new Set([
      ...Object.keys(defaults),
      ...Object.keys(currentSettings),
    ]);
    for (const key of keys) {
      if ((currentSettings[key] ?? undefined) !== (defaults[key] ?? undefined)) {
        return true;
      }
    }
    return false;
  }, []);

  const findMatchingPreset = useCallback(
    (mode, settings) =>
      customPresets.find((p) => {
        if (p.mode !== mode) return false;
        const keys = new Set([
          ...Object.keys(p.settings),
          ...Object.keys(settings),
        ]);
        for (const key of keys) {
          if (
            (settings[key] ?? undefined) !== (p.settings[key] ?? undefined)
          ) {
            return false;
          }
        }
        return true;
      }) || null,
    [customPresets],
  );

  return {
    customPresets,
    recentPresets,
    loading,
    error,
    clearError,
    savePreset,
    updatePreset,
    deletePreset,
    touchPreset,
    getPresetsForMode,
    isDirty,
    findMatchingPreset,
  };
}
